import { AfterViewInit, Component, EventEmitter, HostListener, OnInit, Output } from '@angular/core';
import { Router } from "@angular/router";
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { COMPANY_ID, COUNTRIES, GlobalConstants } from '../shared/global-constants';
import { StateRegion } from '../shared/model/state-region';
import { ManagedContentService } from '../shared/service/managed-content.service';
import { CmElement, CmHelp, CmSection, ContentType } from '../shared/model/cm/managed-content.model';
import { fromEvent, Observable, of, Subscription } from 'rxjs';
import { UserSessionService } from '../shared/service/user-session.service';
import { DeactivateGuard } from '../deactivate.guard';
import { QuestionService } from '../shared/service/question.service';
import { AppInsightsApiService} from "../shared/service/app-insights-api.service";
import { ViewportScroller } from '@angular/common';
import { Notice } from '../shared/model/session/notice';
import { PagetitleService } from '../shared/service/pagetitle.service';
import { AuthGuardService } from '../shared/service/auth-guard.service';
import { ModalService } from '../shared/service/modal.service';
import { WindowRef } from '../shared/service/window.service';
import { FormUtils } from '../shared/helper/form-utils';
import { AppInsightsLoggerService } from '../shared/service/app-insights-logger.service';
import { GoogleAnalyticsService } from '../shared/service/google-analytics.service';
import { CmDialog } from '../shared/model/cm-dialog';
import { Options } from '../shared/model/options';
import { FormControlUtilitiesService } from '../shared/service/form-control-utilities.service';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ContinueCancelComponent } from '../continue-cancel/continue-cancel.component';
import { AmuTimerService } from '../shared/service/amu-timer.service';
import { ApplicantMhs } from '../shared/model/session/applicant-mhs';

@Component({
  selector: 'app-state-of-residence',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ContinueCancelComponent],
  templateUrl: './state-of-residence.component.html',
  styleUrls: ['./state-of-residence.component.scss'],
  providers: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class StateOfResidenceComponent implements OnInit, AfterViewInit, DeactivateGuard {
  @Output() cancelEmitter = new EventEmitter<void>();
  resizeObservable$: Observable<Event>;
  resizeSubscription$: Subscription;
  public innerWidth: number = window.innerWidth;
  public isMobile: boolean = false;
  

  public dropdownWidth: number = 416;
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.dropdownWidth = document.getElementById('state').clientWidth;
  }

  @HostListener('window:load',['$event'])
  onPageLoad(event: Event) {
    this.dropdownWidth = document.getElementById('state').clientWidth;
  }

  public readonly pageIllustrationUrl = 'assets/Illustration-Footer-Base-Mountains.svg';

  private cancelContent: CmHelp = this.cmService.getContentById(ContentType.HELP, "lose-data-warning");
  public modalContentHeading: string = this.cancelContent.title;
  public modalContentBody: string = FormUtils.sanitize(this.cancelContent.body);
  
  public leftPaddingString: string = 'leftPadding';
  public countryLabels = [];
  public stateRegionListLabels = [];
  public stateRegionNMListLabels = [];
  public selectTextState: string = 'State';

  errorModalDialogContent: CmDialog;
  errorModalContent: Notice;
  public errorModalContentHeading: string = "";
  public errorModalContentBody: string = "";

  isFormSubmitted: boolean = false;
  resetSubmitted: boolean = false;

  stateForm: UntypedFormGroup;
  countryElement: CmElement = this.cmService.getContentById(ContentType.ELEMENT, "state-region");
  isValidatingMhs: boolean;
  didQuestionsLoad: boolean;
  systemErrorContent: Notice;
  stateElement: CmElement = this.cmService.getContentById(ContentType.ELEMENT, "state");
  formSection: CmSection  = this.cmService.getContentById(ContentType.SECTION, "set-up-form");
  pageTitle: string;
  intro: string;
  countryLabel: string;

  public prismCountryId: string = "country";
  public prismCountryLabel: string = this.countryElement?.label;

  public prismCountryOptions: Options[] = [
    {
      options: GlobalConstants.RESIDENCE_COUNTRIES_PRISM
    }
  ];

  public prismStateId: string = "state";
  public prismStateLabel: string = this.stateElement?.label;
  public prismStateErrorMessage: string = this.stateElement?.error;

  private subscription: Subscription;

  public readonly US_STATES = GlobalConstants.US_STATES;
  public readonly CA_PROVINCES = GlobalConstants.CANADIAN_PROVINCES;
  public readonly COUNTRIES = GlobalConstants.RESIDENCE_COUNTRIES;
  public stateRegionList: Array<StateRegion> = GlobalConstants.US_STATES;
  public stateRegionNMList: Array<StateRegion> = GlobalConstants.US_STATES_NM
  public countryList: Array<StateRegion> = this.COUNTRIES;
  public displayCountryList: boolean = true;

  // GA4 Custom events
  public submitResidence = {
    action: "button_click",
    category: "residence",
    name: "submit",
  }

  public cancelResidence = {
    action: "button_click",
    category: "residence",
    name: "cancel",
  }

  constructor(private router: Router, private formBuilder: UntypedFormBuilder,
              private cmService: ManagedContentService,
              private userSessionService: UserSessionService,
              private questionService: QuestionService,
              private appInsightsApiService: AppInsightsApiService,
              private googleAnalyticsService: GoogleAnalyticsService,
              private scroller: ViewportScroller,
              private pagetitleService: PagetitleService,
              private authGuardService: AuthGuardService,
              private modalService: ModalService,
              private windowService: WindowRef,
              private appInsightsLoggerService: AppInsightsLoggerService,
              public formControlUtilitiesService: FormControlUtilitiesService,
              private amuTimerService: AmuTimerService
              ) {

              }
  ngAfterViewInit(){
    this.checkIsMobile();
  }
  ngOnInit() {
    if(this.userSessionService.data.sessionEnded){
      this.router.navigate(['/instructions']);
    }
    
    // Skip state-of-residence page when continuing from confirmation
    if (this.userSessionService.data.isContinuingFromConfirmation && this.userSessionService.data.preservedStateData) {
      // Restore preserved state data
      this.userSessionService.data.state = this.userSessionService.data.preservedStateData.state;
      this.userSessionService.data.country = this.userSessionService.data.preservedStateData.country;
      
      // Check if esign-disclosure is required for this state
      if (this.userSessionService.data.state === 'MA' || 
          (this.userSessionService.data.state === 'CA' && this.userSessionService.data.companyId === COMPANY_ID.SI)) {
        this.router.navigate(['/esign-disclosure']);
      } else {
        this.router.navigate(['/applicant-type']);
      }
      return;
    }
    
    this.appInsightsApiService.startNavigationEvent(this.router.url);

    if(this.userSessionService.data.companyId == COMPANY_ID.NM){
      this.displayCountryList = false;
      this.userSessionService.data.country = COUNTRIES.US;
      this.stateRegionList = this.stateRegionNMList;
      this.leftPaddingString = 'leftPaddingNM';
    } else {
      this.leftPaddingString = 'leftPadding';
    }

    if(this.userSessionService.data.companyId && this.userSessionService.data.companyId == COMPANY_ID.YI){

      this.isFormSubmitted=true;
      this.userSessionService.data.state = 'NY';
      this.userSessionService.data.country = 'US';

      this.getMhsQuestions();

      this.router.navigate(['/applicant-type']);
      const navigation = this.router.getCurrentNavigation();
      if (navigation) {
        this.appInsightsApiService.startNavigationEvent(navigation.extractedUrl.toString());
      }
    }

    this.amuTimerService.startOrStopTimer(true);
    this.amuTimerService.startWatching();

    this.stateForm = this.formBuilder.group({
      'state': new UntypedFormControl('', [Validators.required]),
      'country': new UntypedFormControl('US')
    });

    if(this.userSessionService.data.companyId === COMPANY_ID.NM){
      let systemError = this.cmService.getContentById(ContentType.HELP,"system-error-nm");
      this.errorModalContent = {
        id: systemError.id,
        title: systemError.title,
        body: systemError.body
      };
      this.errorModalContentHeading = this.errorModalContent.title;
      this.errorModalContentBody = FormUtils.sanitize(this.errorModalContent.body);
    } else {
      let systemError = this.cmService.getContentById(ContentType.HELP,"system-error");
      this.errorModalContent = {
        id: systemError.id,
        title: systemError.title,
        body: systemError.body
      };
      this.errorModalContentHeading = this.errorModalContent.title;
      this.errorModalContentBody = FormUtils.sanitize(this.errorModalContent.body);
    }

    if(this.errorModalContent){
      this.errorModalDialogContent = {
        id: "errorModal",
        heading: this.errorModalContent.title,
        body: this.errorModalContent.body,
        size: "medium",
      };
      this.errorModalContentHeading = this.errorModalContent.title;
      this.errorModalContentBody = FormUtils.sanitize(this.errorModalContent.body);

      this.resizeObservable$ = fromEvent(window, 'resize')
      this.resizeSubscription$ = this.resizeObservable$.subscribe( evt => {
        this.checkIsMobile();
      });
    }

    this.pageTitle = this.formSection.title;
    this.intro = this.countryElement.label;

    this.stateForm.get('country').valueChanges.subscribe(x => {
      if(this.stateForm.get("country").value != 'US'){
        this.stateForm.get('state').clearValidators();
        this.stateForm.get('state').updateValueAndValidity();
      } else {
        this.stateForm.get("state").setValidators([Validators.required]);
        this.stateForm.get("state").updateValueAndValidity();
      }
   })
   if(this.userSessionService.data.companyId === COMPANY_ID.NM){
    let systemError = this.cmService.getContentById(ContentType.HELP,"system-error-nm");
    this.systemErrorContent = {
      id: systemError.id,
      title: systemError.title,
      body: systemError.body
    };
  } else {
    let systemError = this.cmService.getContentById(ContentType.HELP,"system-error");
    this.systemErrorContent = {
      id: systemError.id,
      title: systemError.title,
      body: systemError.body
    };
  }

   this.isValidatingMhs = false;

   if(this.pagetitleService){
    this.pagetitleService.setPageTitle('State of Residence');
   }

    this.appInsightsApiService.endNavigationEvent(this.router.url);

    setTimeout( ()=>{
      this.onScrollToTop();
      }, 100);

    this.countryLabels = this.countryList.map(country => country.label); 
    this.stateRegionListLabels = [this.selectTextState].concat(this.stateRegionList.map(state => state.label));
    this.stateRegionNMListLabels = [this.selectTextState].concat(this.stateRegionNMList.map(stateNM => stateNM.label));
  }

  get isFormValidating() {
    return this.isValidatingMhs;
  }

  get state(): any { return this.stateForm.get('state'); }
  get country(): any { return this.stateForm.get('country'); }

  // Event Handlers
  handleOptionSelectedCountry(event: Event, key: string) {

    const customEvent = event as CustomEvent;
    let optSelected = customEvent.detail;
    let optValue = this.getCountryValue(optSelected);
    this.stateForm.get(key).setValue(optValue);
  }

  getCountryValue(optSelected: string){
    for(let country of this.countryList){
      if(country.label === optSelected){
        return country.abbreviation;
      }
    }
    return null;
  }

  getCountryLabel(optSelected: string){
    for(let country of this.countryList){
      if(country.abbreviation === optSelected){
        return country.label;
      }
    }
    return null;
  }

  handleOptionSelectedState(event: Event, key: string) {

    const customEvent = event as CustomEvent;
    let optSelected = customEvent.detail;
    let optValue = this.getStateValue(optSelected);
    this.stateForm.get(key).setValue(optValue);
  }

  getStateValue(optSelected: string){
    for(let state of this.stateRegionList){
      if(state.label === optSelected){
        return state.abbreviation;
      }
    }

    if(optSelected != null && optSelected != undefined && optSelected != ""){
      return this.stateRegionList[0].abbreviation;
    } else {
      return null;
    }
  }

  getStateLabel(optSelected: string){
    for(let state of this.stateRegionList){
      if(state.abbreviation === optSelected){
        return state.label;
      }
    }
    return null;
  }

  getMhsQuestions(){
    this.questionService.getMhsQuestions(this.userSessionService.data.state, this.userSessionService.data.policy, this.userSessionService.data.companyId == COMPANY_ID.YI? 'SNY': this.userSessionService.data.companyId).subscribe( newData => {
      if(newData){
        this.userSessionService.data.formNumber = newData.formNumber;
        let isNewGenericDynamicForm: boolean | string = FormUtils.stringToBoolean(newData.newGenericDynamicForm);
        if(isNewGenericDynamicForm === true){
          this.userSessionService.data.isGenericDynamicForm = true;
          this.userSessionService.data.mhsApplication.isNewGenericDynamicForm = true;
        } else {
          this.userSessionService.data.isGenericDynamicForm = false;
          this.userSessionService.data.mhsApplication.isNewGenericDynamicForm = false;
        }
        this.userSessionService.data.notices = newData.notices;
        this.userSessionService.data.newMhsQuestions = newData.medicalHistoryQuestions;

        this.checkForFraudNotice(newData.notices);

        if(this.userSessionService.data.newMhsQuestions && this.userSessionService.data.companyId != COMPANY_ID.YI){
          this.executeAfterQuestionsAreLoaded();
          this.isValidatingMhs = true;
        }
      } else {
        this.appInsightsLoggerService.log('error', 'Error log from StateOfResidence.component', `No data returned from MHS Questions API.`);
        this.openErrorPrompt()
      }
    },
      (error: any) => {
        this.appInsightsLoggerService.log('error', 'Error log from StateOfResidence.component', `Error fetching MHS Questions: ${error}`);
        this.openErrorPrompt();
      });
  }

  openErrorPrompt(){
    this.cancelContent = this.cmService.getContentById(ContentType.HELP, "system-error");
    this.modalContentBody = FormUtils.sanitize(this.cancelContent.body);  
    this.modalContentHeading = this.cancelContent.title;
    this.modalContentBody = FormUtils.sanitize(this.errorModalDialogContent.body);

    this.modalService.showModal('prism-ui-modal-error-modal'); // Use the service to show the modal
  }
  onSubmit() {

    this.googleAnalyticsService.sendCustomEvent(this.submitResidence);
     this.isFormSubmitted=true;

    if(this.isFormValidating) {
      return;
    }

    if(this.state && this.state.pristine){
      this.state.markAsTouched();
      this.state.updateValueAndValidity();
    }
    if(this.country && this.country.pristine){
      this.country.markAsTouched();
      this.country.updateValueAndValidity();
    }
    if(this.stateForm.valid) {
      this.userSessionService.data.country = this.stateForm.get('country').value;
      if(this.userSessionService.data.country != 'US'){
        this.userSessionService.data.state = this.stateForm.get('country').value;
      } else {
        this.userSessionService.data.state = this.stateForm.get('state').value;
      }
      this.setApplicantStateOfResidence();
      this.getMhsQuestions();
    }
  }

  setApplicantStateOfResidence(){
    // Only update state for US countries, non-US countries use the country value as state
    if(this.userSessionService.data.country === 'US') {
      this.userSessionService.data.state = this.stateForm.get('state').value;
    }
    let applicant = new ApplicantMhs();
    applicant.stateOfResidence = this.userSessionService.data.state;
    if(this.userSessionService.data.mhsApplication != undefined){
      let applicant = new ApplicantMhs();
      applicant.stateOfResidence = this.userSessionService.data.state;
      this.userSessionService.data.mhsApplication.applicant = applicant;
    }
  }

  checkForFraudNotice(notices: Notice[]){
    for(let [key, value] of Object.entries(notices)){
      if(key === 'fraudNotices'){
        if(value.body != ""){
          this.userSessionService.data.hasFraudNotice = true;
        } else {
          this.userSessionService.data.hasFraudNotice = false;
        }
      }
    }
  }

  executeAfterQuestionsAreLoaded(){
    this.isValidatingMhs = false;

    const state = this.stateForm.get('state').value;
    const isEsignDisclosureState = state == 'MA' || (state == 'CA' && this.userSessionService.data.companyId === COMPANY_ID.SI);
    
    if(isEsignDisclosureState){
      this.router.navigate(['/esign-disclosure']);
      this.resetSubmitted = true;
    }
    else {
      this.router.navigate(['/applicant-type']);
      this.resetSubmitted = true;
    }
    if(!this.resetSubmitted){
      this.isFormSubmitted = false;
      this.resetSubmitted = false;
    }
    const navigation = this.router.getCurrentNavigation();
    if (navigation) {
      this.appInsightsApiService.startNavigationEvent(navigation.extractedUrl.toString());
    }
  }

  onCancel(){
    this.googleAnalyticsService.sendCustomEvent(this.cancelResidence);
    this.cancelEmitter.emit();
    this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal
  }

  onCancelModal(modalName: string){
    const modalElement =
      this.windowService.nativeDocument.getElementById(modalName);

    if (modalElement && modalElement.close) {
      modalElement.close();
    } else {
      this.appInsightsLoggerService.log('info', 'Info log from StateOfResidence.component', `Modal with id ${modalName} not found or doesn't support close method.`);
    }
  }

  leaveAnyway(){
    this.userSessionService.clearSession();
    this.userSessionService.data.sessionEnded = true;
    this.authGuardService.cancelLeaveAnyway();
    this.router.navigate(['/instructions']);
  }

  canDeactivate(){
    // Skip cancel modal when continuing to new application
    if (this.userSessionService.data.isContinuingFromConfirmation) {
      return of(true);
    }
    
    if (!this.isFormSubmitted && !this.userSessionService.data.sessionEnded){
      this.modalService.showModal('prism-ui-modal-cancel-modal'); // Use the service to show the modal
      return this.userSessionService.data.sessionEnded;
    } else {
      return of(true);
    }
  }

  onScrollToTop(){
    if(this.scroller){
      this.scroller.setOffset([0, 200]);
      this.scroller.scrollToAnchor('topOfPage');
    }
  }

  checkIsMobile(){
    this.innerWidth = window.innerWidth;
      if(this.innerWidth < 768){
        this.isMobile = true;
      } else {
        this.isMobile = false;
      }
  }

}
